<template>
  <div>
    <h3 class="text-h6 mb-4">Configuración de Columnas de la Tabla</h3>
    
    <v-alert type="info" text dense class="mb-4">
      Seleccione las columnas que desea mostrar en el remito y configure su orden y apariencia.
    </v-alert>

    <v-simple-table>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">Visible</th>
            <th class="text-left">Columna</th>
            <th class="text-left">Etiqueta</th>
            <th class="text-left">Ancho</th>
            <th class="text-left">Alineación</th>
            <th class="text-left">Orden</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(columna, index) in columnasInternas" :key="columna.campo">
            <td>
              <v-checkbox
                v-model="columna.visible"
                hide-details
                @change="emitirCambios"
              ></v-checkbox>
            </td>
            <td>
              <v-chip small :color="columna.visible ? 'primary' : 'grey'">
                {{ getNombreCampo(columna.campo) }}
              </v-chip>
            </td>
            <td>
              <v-text-field
                v-model="columna.label"
                dense
                hide-details
                :disabled="!columna.visible"
                @input="emitirCambios"
              ></v-text-field>
            </td>
            <td>
              <v-text-field
                v-model="columna.width"
                dense
                hide-details
                placeholder="auto"
                :disabled="!columna.visible"
                @input="emitirCambios"
              ></v-text-field>
            </td>
            <td>
              <v-select
                v-model="columna.align"
                :items="alineaciones"
                dense
                hide-details
                :disabled="!columna.visible"
                @change="emitirCambios"
              ></v-select>
            </td>
            <td>
              <v-btn
                icon
                small
                :disabled="index === 0"
                @click="moverArriba(index)"
              >
                <v-icon>mdi-arrow-up</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                :disabled="index === columnasInternas.length - 1"
                @click="moverAbajo(index)"
              >
                <v-icon>mdi-arrow-down</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-row class="mt-4">
      <v-col cols="12" md="4">
        <v-text-field
          v-model.number="itemsPorPagina"
          type="number"
          label="Items por página"
          outlined
          dense
          @input="emitirCambios"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="tamanoDatosInterno"
          label="Tamaño fuente datos tabla"
          outlined
          dense
          placeholder="9.1px"
          hint="Ej: 9px, 9.1px"
          persistent-hint
          @input="emitirTamanoDatos"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-switch
          v-model="mostrarValorAsegurado"
          label="Mostrar valor asegurado"
          @change="emitirCambios"
        ></v-switch>
      </v-col>
    </v-row>

    <v-alert v-if="!hayColumnasVisibles" type="warning" text dense>
      <strong>Atención:</strong> Debe haber al menos una columna visible.
    </v-alert>
  </div>
</template>

<script>
export default {
  name: 'ColumnasEditor',
  
  props: {
    columnas: {
      type: Array,
      required: true
    },
    tamanoDatosTabla: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      columnasInternas: [],
      itemsPorPagina: 14,
      tamanoDatosInterno: '',
      mostrarValorAsegurado: true,
      alineaciones: [
        { text: 'Izquierda', value: 'left' },
        { text: 'Centro', value: 'center' },
        { text: 'Derecha', value: 'right' }
      ],
      nombresCampos: {
        codigo: 'Código',
        articulo: 'Artículo',
        descripcion: 'Descripción',
        partida: 'Partida',
        despachoPlaza: 'Despacho Plaza',
        bultos: 'Bultos',
        cantidad: 'Cantidad'
      }
    }
  },

  computed: {
    hayColumnasVisibles() {
      return this.columnasInternas.some(c => c.visible)
    }
  },

  watch: {
    columnas: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.columnasInternas = this.normalizarColumnas(newVal)
        }
      }
    },
    tamanoDatosTabla: {
      immediate: true,
      handler(newVal) {
        this.tamanoDatosInterno = newVal || '9px'
      }
    }
  },

  methods: {
    normalizarColumnas(columnas) {
      const columnasNormalizadas = JSON.parse(JSON.stringify(columnas || []))
      const existeBultos = columnasNormalizadas.some(col => col.campo === 'bultos')

      if (!existeBultos) {
        const indiceCantidad = columnasNormalizadas.findIndex(col => col.campo === 'cantidad')
        const nuevaColumnaBultos = {
          campo: 'bultos',
          label: 'Bultos',
          width: '8%',
          align: 'center',
          visible: true,
          order: 0
        }

        if (indiceCantidad >= 0) {
          columnasNormalizadas.splice(indiceCantidad, 0, nuevaColumnaBultos)
        } else {
          columnasNormalizadas.push(nuevaColumnaBultos)
        }
      }

      return columnasNormalizadas.map((col, index) => ({
        ...col,
        order: index + 1
      }))
    },

    getNombreCampo(campo) {
      return this.nombresCampos[campo] || campo
    },

    moverArriba(index) {
      if (index === 0) return
      const temp = this.columnasInternas[index]
      this.$set(this.columnasInternas, index, this.columnasInternas[index - 1])
      this.$set(this.columnasInternas, index - 1, temp)
      this.actualizarOrden()
      this.emitirCambios()
    },

    moverAbajo(index) {
      if (index === this.columnasInternas.length - 1) return
      const temp = this.columnasInternas[index]
      this.$set(this.columnasInternas, index, this.columnasInternas[index + 1])
      this.$set(this.columnasInternas, index + 1, temp)
      this.actualizarOrden()
      this.emitirCambios()
    },

    actualizarOrden() {
      this.columnasInternas.forEach((col, index) => {
        col.order = index + 1
      })
    },

    emitirCambios() {
      this.$emit('update', this.columnasInternas)
    },

    emitirTamanoDatos() {
      this.$emit('update-tamano-datos', this.tamanoDatosInterno)
    }
  }
}
</script>

<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}
</style>
